import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useLocation } from 'react-router-dom';
import { billingAPI } from '../../services/api';
import toast from 'react-hot-toast';
import {
  CreditCardIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ArrowTopRightOnSquareIcon,
} from '@heroicons/react/24/outline';

export default function Billing() {
  const queryClient = useQueryClient();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const paywallParam = location.state?.paywall === true || searchParams.get('paywall') === 'true';
  const [isYearly, setIsYearly] = useState(false);

  const { data: subscription, isLoading: subLoading } = useQuery(
    'subscription',
    billingAPI.getSubscription,
    {
      retry: 1,
      onError: (error) => {
        console.error('Error fetching subscription:', error);
      }
    }
  );

  const { data: usage, isLoading: usageLoading } = useQuery(
    'usage',
    billingAPI.getUsage,
    {
      retry: 1,
      onError: (error) => {
        console.error('Error fetching usage:', error);
      }
    }
  );

  const { data: invoices, isLoading: invoicesLoading } = useQuery(
    'invoices',
    billingAPI.getInvoices,
    {
      retry: 1,
      onError: (error) => {
        console.error('Error fetching invoices:', error);
      }
    }
  );

  const checkoutMutation = useMutation(
    (params) => billingAPI.createCheckout(params),
    {
      onSuccess: (data) => {
        if (data.checkout_url) {
          window.location.href = data.checkout_url;
        }
      },
      onError: (error) => {
        toast.error(error.response?.data?.detail || 'Failed to start checkout');
      }
    }
  );

  const portalMutation = useMutation(
    () => billingAPI.createPortal(window.location.href),
    {
      onSuccess: (data) => {
        if (data.portal_url) {
          window.location.href = data.portal_url;
        }
      },
      onError: (error) => {
        toast.error(error.response?.data?.detail || 'Failed to open billing portal');
      }
    }
  );

  const cancelMutation = useMutation(
    () => billingAPI.cancelSubscription(),
    {
      onSuccess: () => {
        toast.success('Subscription will be canceled at end of billing period');
        queryClient.invalidateQueries('subscription');
      },
      onError: (error) => {
        toast.error(error.response?.data?.detail || 'Failed to cancel subscription');
      }
    }
  );

  const reactivateMutation = useMutation(
    () => billingAPI.reactivateSubscription(),
    {
      onSuccess: () => {
        toast.success('Subscription reactivated');
        queryClient.invalidateQueries('subscription');
      },
      onError: (error) => {
        toast.error(error.response?.data?.detail || 'Failed to reactivate subscription');
      }
    }
  );

  const handleUpgrade = (plan) => {
    checkoutMutation.mutate({
      plan,
      billing_cycle: isYearly ? 'yearly' : 'monthly',
      success_url: `${window.location.origin}/admin/billing?success=true`,
      cancel_url: `${window.location.origin}/admin/billing`
    });
  };

  const handleManageBilling = () => {
    portalMutation.mutate();
  };

  // Check for success parameter in URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('success') === 'true') {
      toast.success('Subscription updated successfully!');
      // Clean up URL
      window.history.replaceState({}, '', '/admin/billing');
      queryClient.invalidateQueries('subscription');
    }
  }, [queryClient]);

  if (subLoading || usageLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <ArrowPathIcon className="w-8 h-8 animate-spin text-cleona-500" />
      </div>
    );
  }

  const usagePercentage = usage ? (usage.current_usage / usage.limit) * 100 : 0;
  const currentPlan = subscription?.plan || 'starter';
  const status = subscription?.status || 'trialing';
  const isActive = status === 'active' ||
    (status === 'trialing' && subscription?.trial_ends_at && new Date(subscription.trial_ends_at) > new Date());
  const isPaywalled = paywallParam && !isActive;

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'text-green-600 bg-green-100';
      case 'trialing':
        return 'text-blue-600 bg-blue-100';
      case 'past_due':
        return 'text-red-600 bg-red-100';
      case 'canceled':
        return 'text-gray-600 bg-gray-100';
      default:
        return 'text-yellow-600 bg-yellow-100';
    }
  };

  const plans = [
    {
      id: 'starter',
      name: 'Starter',
      monthlyPrice: 39,
      yearlyPrice: 390,
      features: [
        '1 assistant',
        'Up to 50 resources',
        '2,000 messages/month',
        'Basic customization',
        'Email support',
      ],
    },
    {
      id: 'pro',
      name: 'Pro',
      monthlyPrice: 99,
      yearlyPrice: 990,
      features: [
        '1 assistant',
        'Up to 300 resources',
        '10,000 messages/month',
        'Full customization',
        'Priority support',
        'Analytics dashboard',
      ],
      popular: true,
    },
    {
      id: 'enterprise',
      name: 'Enterprise',
      monthlyPrice: 500,
      yearlyPrice: 5000,
      features: [
        'Unlimited assistants',
        '2,000+ resources',
        '50,000+ messages/month',
        'API access',
        'SSO/SAML',
        'Dedicated support',
        'Custom integrations',
      ],
    },
  ];

  return (
    <div className="space-y-6 max-w-6xl mx-auto">
      {isPaywalled && (
        <div className="bg-red-50 border border-red-200 rounded-xl px-6 py-4">
          <div className="flex items-start gap-3">
            <ExclamationTriangleIcon className="h-6 w-6 text-red-500 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-sm font-semibold text-red-800">
                {status === 'trial_expired'
                  ? 'Your free trial has expired'
                  : status === 'canceled'
                  ? 'Your subscription has been canceled'
                  : status === 'past_due'
                  ? 'Your payment is past due'
                  : 'Subscription required'}
              </h3>
              <p className="text-sm text-red-700 mt-1">
                Please choose a plan below to continue using the platform.
              </p>
            </div>
          </div>
        </div>
      )}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Billing & Subscription</h1>
        {subscription?.stripe_customer_id && (
          <button
            onClick={handleManageBilling}
            disabled={portalMutation.isLoading}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <CreditCardIcon className="w-5 h-5" />
            {portalMutation.isLoading ? 'Loading...' : 'Manage Billing'}
            <ArrowTopRightOnSquareIcon className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Current Plan Card */}
      <div className="bg-white rounded-xl shadow-sm border p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Current Plan</h2>
            <div className="mt-2 flex items-center gap-3">
              <span className="text-3xl font-bold text-gray-900 capitalize">{currentPlan}</span>
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(status)}`}>
                {status}
              </span>
            </div>
            {subscription?.trial_ends_at && status === 'trialing' && (
              <p className="mt-2 text-sm text-gray-500">
                Trial ends: {new Date(subscription.trial_ends_at).toLocaleDateString()}
              </p>
            )}
            {subscription?.current_period_end && status === 'active' && (
              <p className="mt-2 text-sm text-gray-500">
                Next billing: {new Date(subscription.current_period_end).toLocaleDateString()}
              </p>
            )}
            {subscription?.cancel_at_period_end && (
              <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
                <ExclamationTriangleIcon className="w-4 h-4" />
                Cancels at end of billing period
              </p>
            )}
          </div>
          <div>
            {subscription?.cancel_at_period_end ? (
              <button
                onClick={() => reactivateMutation.mutate()}
                disabled={reactivateMutation.isLoading}
                className="px-4 py-2 bg-cleona-600 text-white rounded-lg hover:bg-cleona-700 transition-colors"
              >
                {reactivateMutation.isLoading ? 'Reactivating...' : 'Reactivate'}
              </button>
            ) : subscription?.stripe_subscription_id ? (
              <button
                onClick={() => {
                  if (window.confirm('Are you sure you want to cancel your subscription? You will retain access until the end of your billing period.')) {
                    cancelMutation.mutate();
                  }
                }}
                disabled={cancelMutation.isLoading}
                className="px-4 py-2 text-red-600 border border-red-300 rounded-lg hover:bg-red-50 transition-colors"
              >
                {cancelMutation.isLoading ? 'Canceling...' : 'Cancel Subscription'}
              </button>
            ) : null}
          </div>
        </div>
      </div>

      {/* Trial Info Card - Show for trialing users with Stripe subscription */}
      {status === 'trialing' && subscription?.stripe_subscription_id && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0">
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-blue-900">You're on a Free Trial</h3>
              <p className="mt-1 text-sm text-blue-700">
                Your trial ends on <span className="font-medium">{subscription?.trial_ends_at ? new Date(subscription.trial_ends_at).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }) : 'soon'}</span>.
                You won't be charged until your trial ends.
              </p>
              <div className="mt-4 flex items-center gap-4">
                <button
                  onClick={handleManageBilling}
                  disabled={portalMutation.isLoading}
                  className="text-sm font-medium text-blue-700 hover:text-blue-800"
                >
                  Update payment method
                </button>
                <span className="text-blue-300">|</span>
                <button
                  onClick={() => {
                    if (window.confirm('Are you sure you want to cancel your trial? You will lose access at the end of your trial period.')) {
                      cancelMutation.mutate();
                    }
                  }}
                  disabled={cancelMutation.isLoading}
                  className="text-sm font-medium text-blue-700 hover:text-blue-800"
                >
                  {cancelMutation.isLoading ? 'Canceling...' : 'Cancel trial'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Usage Card */}
      <div className="bg-white rounded-xl shadow-sm border p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Monthly Usage</h2>
        <div className="space-y-4">
          <div>
            <div className="flex justify-between text-sm mb-2">
              <span className="text-gray-600">Messages used</span>
              <span className="font-medium">
                {usage?.current_usage?.toLocaleString() || 0} / {usage?.limit?.toLocaleString() || 0}
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div
                className={`h-3 rounded-full transition-all ${
                  usagePercentage > 90 ? 'bg-red-500' :
                  usagePercentage > 75 ? 'bg-yellow-500' :
                  'bg-cleona-500'
                }`}
                style={{ width: `${Math.min(usagePercentage, 100)}%` }}
              />
            </div>
          </div>
          {usagePercentage > 80 && (
            <div className="flex items-center gap-2 text-yellow-700 bg-yellow-50 px-4 py-3 rounded-lg">
              <ExclamationTriangleIcon className="w-5 h-5" />
              <span className="text-sm">
                You're approaching your monthly limit. Consider upgrading for more messages.
              </span>
            </div>
          )}
          {usage?.period_end && (
            <p className="text-sm text-gray-500">
              Resets on {new Date(usage.period_end).toLocaleDateString()}
            </p>
          )}
        </div>
      </div>

      {/* Upgrade Options */}
      {currentPlan !== 'enterprise' && (
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-gray-900">Upgrade Your Plan</h2>
            <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setIsYearly(false)}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  !isYearly ? 'bg-white shadow-sm text-gray-900' : 'text-gray-600'
                }`}
              >
                Monthly
              </button>
              <button
                onClick={() => setIsYearly(true)}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  isYearly ? 'bg-white shadow-sm text-gray-900' : 'text-gray-600'
                }`}
              >
                Yearly
                <span className="ml-1 text-xs text-green-600">(Save 17%)</span>
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {plans.map((plan) => {
              const isCurrent = plan.id === currentPlan;
              const price = isYearly ? plan.yearlyPrice : plan.monthlyPrice;
              const period = isYearly ? '/year' : '/month';

              return (
                <div
                  key={plan.id}
                  className={`relative rounded-xl border-2 p-6 ${
                    plan.popular ? 'border-cleona-500 shadow-lg' : 'border-gray-200'
                  } ${isCurrent ? 'bg-gray-50' : 'bg-white'}`}
                >
                  {plan.popular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <span className="bg-cleona-500 text-white text-xs font-semibold px-3 py-1 rounded-full">
                        MOST POPULAR
                      </span>
                    </div>
                  )}

                  <h3 className="text-lg font-semibold text-gray-900">{plan.name}</h3>
                  <div className="mt-2">
                    <span className="text-3xl font-bold text-gray-900">${price}</span>
                    <span className="text-gray-500">{period}</span>
                  </div>

                  <ul className="mt-4 space-y-2">
                    {plan.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-sm text-gray-600">
                        <CheckCircleIcon className="w-4 h-4 text-cleona-500 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>

                  {plan.id === 'enterprise' ? (
                    <a
                      href="mailto:sales@cleona.io?subject=Enterprise%20Plan%20Inquiry&body=Hi%2C%0A%0AI'm%20interested%20in%20upgrading%20to%20Cleona's%20Enterprise%20plan.%0A%0AOrganization%3A%20%0ACurrent%20plan%3A%20%0AUse%20case%3A%20%0A%0AThank%20you!"
                      className="mt-6 w-full py-2.5 rounded-lg font-medium transition-colors bg-gray-900 text-white hover:bg-gray-800 block text-center"
                    >
                      Contact Us
                    </a>
                  ) : (
                    <button
                      onClick={() => !isCurrent && handleUpgrade(plan.id)}
                      disabled={isCurrent || checkoutMutation.isLoading}
                      className={`mt-6 w-full py-2.5 rounded-lg font-medium transition-colors ${
                        isCurrent
                          ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                          : plan.popular
                          ? 'bg-cleona-600 text-white hover:bg-cleona-700'
                          : 'bg-gray-900 text-white hover:bg-gray-800'
                      }`}
                    >
                      {isCurrent ? 'Current Plan' : checkoutMutation.isLoading ? 'Loading...' : 'Upgrade'}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Invoice History */}
      <div className="bg-white rounded-xl shadow-sm border p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Invoice History</h2>
        {invoicesLoading ? (
          <div className="flex justify-center py-8">
            <ArrowPathIcon className="w-6 h-6 animate-spin text-gray-400" />
          </div>
        ) : invoices?.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left text-sm text-gray-500 border-b">
                  <th className="pb-3 font-medium">Date</th>
                  <th className="pb-3 font-medium">Amount</th>
                  <th className="pb-3 font-medium">Status</th>
                  <th className="pb-3 font-medium"></th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((invoice) => (
                  <tr key={invoice.id} className="border-b last:border-b-0">
                    <td className="py-4 text-sm text-gray-900">
                      {new Date(invoice.created_at).toLocaleDateString()}
                    </td>
                    <td className="py-4 text-sm text-gray-900">
                      ${(invoice.amount_cents / 100).toFixed(2)} {invoice.currency?.toUpperCase()}
                    </td>
                    <td className="py-4">
                      <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${
                        invoice.status === 'paid' ? 'bg-green-100 text-green-800' :
                        invoice.status === 'open' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {invoice.status}
                      </span>
                    </td>
                    <td className="py-4 text-right">
                      {invoice.invoice_url && (
                        <a
                          href={invoice.invoice_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-cleona-600 hover:text-cleona-700 flex items-center gap-1 justify-end"
                        >
                          View
                          <ArrowTopRightOnSquareIcon className="w-4 h-4" />
                        </a>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-gray-500 text-center py-8">No invoices yet</p>
        )}
      </div>
    </div>
  );
}
